import { useState } from 'react';
import { EmailService } from '../../../services/EmailService';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Loader2, Send } from 'lucide-react';
import { Textarea } from '../../../components/ui/TextArea';

interface CommunicationPanelProps {
  eventId: string; // Ahora es requerido
  eventTitle?: string;
  eventImage?: string;
}

export function CommunicationPanel({ 
  eventId, 
  eventTitle,
  eventImage
}: CommunicationPanelProps) {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSendMessage = async () => {
    // Reset states
    setError('');
    setSuccess('');

    // Validation
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }

    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    if (!user) {
      setError('You must be logged in to send messages');
      return;
    }

    if (!eventId) {
      setError('Event ID is required');
      return;
    }

    setIsLoading(true);

    try {
      const messageDetails = {
        subject: subject.trim(),
        message: message.trim(),
        sender_name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
        image_url: eventImage || '',
        sender_email: user.email || '',
        event_title: eventTitle,
        event_id: eventId
      };

      const { error: emailError, sent_count } = await EmailService.sendInstantMessageToEventUsers(
        eventId,
        messageDetails
      );

      if (emailError) {
        setError('Failed to send message. Please try again.');
        return;
      }

      setSuccess(`Message sent successfully to ${sent_count} confirmed attendees!`);
      
      // Reset form
      setSubject('');
      setMessage('');

    } catch (error) {
      console.error('Error sending message:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Instant message</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{success}</p>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Subject *
          </p>
          <Input
            type="text"
            placeholder="Subject"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              if (error) setError('');
            }}
            disabled={isLoading}
          />
        </div>
        
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Message *
          </p>
          <Textarea
            placeholder="Type the message..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (error) setError('');
            }}
            rows={4}
            className="resize-none"
            disabled={isLoading}
          />
        </div>
        
        <div className="flex justify-end">
          <Button
            onClick={handleSendMessage}
            disabled={isLoading}
            className="bg-gray-900 hover:bg-gray-800"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send message
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}